import { writable, get, type Writable } from 'svelte/store';
import { gen2Request } from 'src/services/gen_2_http_reqs';

export type VialSlot = 'left' | 'right';

export type VialInfo = {
	name: string;
	colorBrightness?: number;
	colorRgb?: number[];
};

type DeviceVialInfo = Record<VialSlot, VialInfo | null>;

const createVialInfoStore = () => {
	const { subscribe, update }: Writable<Record<string, DeviceVialInfo>> = writable({});
	const inflight: Map<string, Promise<DeviceVialInfo>> = new Map();
	const FETCH_DELAY_MS = 300;

	const setDeviceInfo = (deviceId: string, info: Partial<DeviceVialInfo>) => {
		update((state) => {
			const current = state[deviceId] || { left: null, right: null };
			state[deviceId] = {
				left: info.left ?? current.left,
				right: info.right ?? current.right,
			};
			return state;
		});
	};

	const getDeviceInfo = (deviceId: string): DeviceVialInfo | undefined => {
		const state = get({ subscribe });
		return state[deviceId];
	};

	const ensureFetched = async (device: any): Promise<DeviceVialInfo> => {
		const deviceId: string = device?.id;
		const existing = getDeviceInfo(deviceId);
		if (existing && (existing.left || existing.right)) return existing;

		const inProgress = inflight.get(deviceId);
		if (inProgress) return inProgress;

		const promise = (async () => {
			// Short delay to coalesce bursts and allow other state to arrive
			if (FETCH_DELAY_MS > 0) {
				await new Promise((resolve) => setTimeout(resolve, FETCH_DELAY_MS));
				const recheck = getDeviceInfo(deviceId);
				if (recheck && (recheck.left || recheck.right)) return recheck;
			}
			const res: any = await gen2Request(device, 'cury.getVialInfo', { id: 0 });
			const container = res?.result ?? res?.data;
			const extract = (slot: VialSlot): VialInfo | null => {
				const _slot = container?.[slot];
				if (!_slot) return null;
				const name = _slot?.product_name || _slot?.name;
				if (!name) return null;
				const colorBrightness =
					typeof _slot?.color_intensity?.brightness === 'number'
						? _slot.color_intensity.brightness
						: undefined;
				const colorRgb = Array.isArray(_slot?.color_intensity?.rgb)
					? _slot.color_intensity.rgb
					: undefined;
				return { name, colorBrightness, colorRgb };
			};

			const info: DeviceVialInfo = {
				left: extract('left'),
				right: extract('right'),
			};

			setDeviceInfo(deviceId, info);
			return info;
		})().finally(() => inflight.delete(deviceId));

		inflight.set(deviceId, promise);
		return promise;
	};

	return {
		subscribe,
		setDeviceInfo,
		getDeviceInfo,
		ensureFetched,
	};
};

export const vialInfoStore = createVialInfoStore();
